import { createContext, useContext, useState, useEffect } from 'react';
import axios from 'axios';
import { storage, checkStorageCompatibility } from './utils/storageCompat';
import { API_URL } from './config';
import logger from '@/utils/logger';

const AuthContext = createContext(null);

const API = API_URL;

// Keys para storage
const TOKEN_KEY = 'token';
const REFRESH_TOKEN_KEY = 'refresh_token';
const OFFLINE_CREDS_KEY = 'offline_credentials';

// Milisegundos hasta que caduque el JWT (null si no se puede leer el payload)
const tokenMsUntilExpiry = (jwt) => {
  try {
    const payload = JSON.parse(atob(jwt.split('.')[1].replace(/-/g, '+').replace(/_/g, '/')));
    return payload.exp ? payload.exp * 1000 - Date.now() : null;
  } catch {
    return null;
  }
};

  
export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [token, setToken] = useState(storage.getItem(TOKEN_KEY));
  const [refreshToken, setRefreshToken] = useState(storage.getItem(REFRESH_TOKEN_KEY));
  const [storageWarning, setStorageWarning] = useState(null);

  // Check storage compatibility on mount
  useEffect(() => {
    const compat = checkStorageCompatibility();
    if (compat.isLimited) {
      setStorageWarning(compat.message);
      console.warn('Storage limited:', compat);
    }
  }, []);

  // Warm-up ping: evita cold-start del backend en Kubernetes (Emergent escala a 0
  // tras inactividad). Mientras haya usuario logueado y la pestaña esté visible,
  // hacemos un GET ligero al /api/health cada 4 minutos. Esto mantiene caliente
  // el contenedor del backend para que el siguiente login/petición no espere 10-30s.
  useEffect(() => {
    if (!token) return undefined;
    const ping = async () => {
      if (typeof document !== 'undefined' && document.hidden) return;
      try {
        await fetch(`${API}/health`, { method: 'GET', cache: 'no-store' });
      } catch {
        // Silencioso: si falla, el siguiente intento lo cubrirá
      }
    };
    // Ping inicial al arrancar la sesión, luego cada 4 min
    ping();
    const id = setInterval(ping, 4 * 60 * 1000);
    return () => clearInterval(id);
  }, [token]);

  // Setup axios interceptor for adding token to requests and handling 401 errors
  useEffect(() => {
    if (token) {
      axios.defaults.headers.common['Authorization'] = `Bearer ${token}`;
      loadUser();
    } else {
      setLoading(false);
    }

    // Interceptor para manejar errores (401, 502, 524, etc.)
    const interceptor = axios.interceptors.response.use(
      (response) => response,
      async (error) => {
        const originalRequest = error.config;
        const status = error.response?.status;
        
        // CRÍTICO: Para errores 502/524 (servidor sobrecargado/timeout), NO hacer logout
        // Solo esperar y reintentar o dejar que el usuario lo intente de nuevo
        if (status === 502 || status === 524 || status === 503 || status === 504) {
          logger.log(`🔴 Error ${status} - Servidor temporalmente no disponible`);
          // NO hacer logout, NO reintentar automáticamente (evita loops)
          return Promise.reject(error);
        }
        
        // Si es 401 y no hemos reintentado ya
        if (status === 401 && !originalRequest._retry) {
          // Don't try to refresh offline tokens
          if (token && token.startsWith('offline_')) {
            logger.log('🔴 Offline token - cannot refresh, staying logged in');
            return Promise.reject(error);
          }
          
          // Evitar retry si la request original era de auth
          const isAuthEndpoint = originalRequest.url?.includes('/auth/');
          if (isAuthEndpoint) {
            logger.log('🔴 Auth endpoint failed - not retrying');
            return Promise.reject(error);
          }
          
          originalRequest._retry = true;
          
          logger.log('⚠️ Token expirado detectado, intentando renovar...');
          
          // Primero intentar con refresh_token (sesión persistente)
          const savedRefreshToken = storage.getItem(REFRESH_TOKEN_KEY);
          if (savedRefreshToken) {
            try {
              logger.log('🔄 Usando refresh_token para renovar sesión...');
              const response = await axios.post(`${API}/auth/refresh-token`, {
                refresh_token: savedRefreshToken
              }, { timeout: 5000 }); // Feb-2026: 10s → 5s (en red mala, 10s×2 = 20s de bloqueo por request)
              
              if (response.data && response.data.access_token) {
                const newToken = response.data.access_token;
                
                // Actualizar el token en storage y estado
                storage.setItem(TOKEN_KEY, newToken);
                setToken(newToken);
                axios.defaults.headers.common['Authorization'] = `Bearer ${newToken}`;
                
                // Actualizar el header de la petición original
                originalRequest.headers['Authorization'] = `Bearer ${newToken}`;
                
                logger.log('✅ Sesión renovada con refresh_token');
                return axios(originalRequest);
              }
            } catch (refreshError) {
              const refreshStatus = refreshError.response?.status;
              // Si es error de servidor (no 401), NO limpiar tokens
              if (refreshStatus === 502 || refreshStatus === 524 || refreshStatus === 503) {
                logger.log('🔴 Server error during refresh - keeping tokens');
                return Promise.reject(error);
              }
              logger.log('❌ Refresh token inválido o expirado');
              // Limpiar refresh token inválido
              storage.removeItem(REFRESH_TOKEN_KEY);
              setRefreshToken(null);
            }
          }
          
          // Si no hay refresh_token o falló, intentar con el token actual (legacy)
          if (token) {
            try {
              const response = await axios.post(`${API}/auth/refresh`, {}, {
                headers: { Authorization: `Bearer ${token}` },
                timeout: 5000 // Feb-2026: 10s → 5s para no bloquear UI en red mala
              });
              
              if (response.data && response.data.access_token) {
                const newToken = response.data.access_token;
                
                storage.setItem(TOKEN_KEY, newToken);
                setToken(newToken);
                axios.defaults.headers.common['Authorization'] = `Bearer ${newToken}`;
                originalRequest.headers['Authorization'] = `Bearer ${newToken}`;
                
                logger.log('✅ Token renovado (legacy)');
                return axios(originalRequest);
              }
            } catch (legacyRefreshError) {
              const legacyStatus = legacyRefreshError.response?.status;
              // Only logout if it's a real auth error, not network/server error
              if (!navigator.onLine || legacyRefreshError.code === 'ERR_NETWORK' || !legacyRefreshError.response) {
                logger.log('🔴 Network error during token refresh - staying logged in');
                return Promise.reject(error);
              }
              if (legacyStatus === 502 || legacyStatus === 524 || legacyStatus === 503) {
                logger.log('🔴 Server error during legacy refresh - keeping tokens');
                return Promise.reject(error);
              }
              
              console.error('❌ Error renovando token, cerrando sesión...');
              logout();
              return Promise.reject(legacyRefreshError);
            }
          }
        }
        
        return Promise.reject(error);
      }
    );

    return () => {
      axios.interceptors.response.eject(interceptor);
    };
  // eslint-disable-next-line react-hooks/exhaustive-deps -- deps: loadUser and logout
  }, [token]);

  // Keep-alive: Renovar token automáticamente cada 25 minutos
  // (access_token dura 30 min, así que renovamos antes de que expire)
  useEffect(() => {
    if (!token || !user) return;
    
    // Don't try to refresh offline tokens
    if (token.startsWith('offline_')) {
      logger.log('🔴 Offline mode - skipping token refresh');
      return;
    }

    const refreshAccessToken = async () => {
      // Skip refresh if offline
      if (!navigator.onLine) {
        logger.log('🔴 Offline - skipping token refresh');
        return;
      }
      
      try {
        logger.log('🔄 Renovando token automáticamente...');
        
        // Primero intentar con refresh_token si existe
        const savedRefreshToken = storage.getItem(REFRESH_TOKEN_KEY);
        if (savedRefreshToken) {
          try {
            const response = await axios.post(`${API}/auth/refresh-token`, {
              refresh_token: savedRefreshToken
            });
            
            const newToken = response.data.access_token;
            storage.setItem(TOKEN_KEY, newToken);
            setToken(newToken);
            axios.defaults.headers.common['Authorization'] = `Bearer ${newToken}`;
            
            logger.log('✅ Token renovado con refresh_token - sesión extendida');
            return;
          } catch (e) {
            logger.log('⚠️ Refresh token falló, intentando método legacy...');
          }
        }
        
        // Fallback: usar el access_token actual
        const response = await axios.post(`${API}/auth/refresh`);
        const newToken = response.data.access_token;
        storage.setItem(TOKEN_KEY, newToken);
        setToken(newToken);
        axios.defaults.headers.common['Authorization'] = `Bearer ${newToken}`;
        
        logger.log('✅ Token renovado (legacy) - sesión extendida');
      } catch (error) {
        console.error('❌ Error renovando token:', error);
        
        // Only logout if it's a real authentication error, not a network error
        if (!navigator.onLine || error.code === 'ERR_NETWORK' || !error.response) {
          logger.log('🔴 Network error during token refresh - staying logged in');
          return;
        }
        
        if (error.response?.status === 401 || error.response?.status === 403) {
          logger.log('Token expirado, cerrando sesión...');
          logout();
        }
      }
    };

    // Renovar token cada 25 minutos (antes de que expire el access_token de 30 min)
    const interval = setInterval(refreshAccessToken, 25 * 60 * 1000); // 25 minutos

    // Jun-2026 (auditoría D-03): iOS congela los timers con la PWA en segundo plano.
    // Al volver a primer plano (o recuperar red) renovamos si el token caduca pronto
    // o ya ha caducado, para que el DJ no acabe en la pantalla de login a media noche.
    let refreshing = false;
    const refreshIfStale = async () => {
      if (refreshing || document.visibilityState === 'hidden') return;
      const currentToken = storage.getItem(TOKEN_KEY);
      if (!currentToken || currentToken.startsWith('offline_')) return;
      const msLeft = tokenMsUntilExpiry(currentToken);
      if (msLeft !== null && msLeft < 30 * 60 * 1000) {
        logger.log(`🔄 Token caduca en ${Math.round(msLeft / 60000)} min — renovando al volver a primer plano`);
        refreshing = true;
        try { await refreshAccessToken(); } finally { refreshing = false; }
      }
    };
    document.addEventListener('visibilitychange', refreshIfStale);
    window.addEventListener('pageshow', refreshIfStale);
    window.addEventListener('online', refreshIfStale);
    window.addEventListener('focus', refreshIfStale);

    return () => {
      clearInterval(interval);
      document.removeEventListener('visibilitychange', refreshIfStale);
      window.removeEventListener('pageshow', refreshIfStale);
      window.removeEventListener('online', refreshIfStale);
      window.removeEventListener('focus', refreshIfStale);
    };
  // eslint-disable-next-line react-hooks/exhaustive-deps -- deps: logout
  }, [token, user]);

  const loadUser = async () => {
    try {
      const response = await axios.get(`${API}/auth/me`, { timeout: 10000 });
      setUser(response.data);
    } catch (error) {
      console.error('Error loading user:', error);
      const status = error.response?.status;
      
      // CRÍTICO: No hacer logout en errores de servidor (502, 524, 503, 504)
      if (status === 502 || status === 524 || status === 503 || status === 504) {
        logger.log(`🔴 Server error ${status} - keeping session, trying offline credentials`);
        // Intentar cargar usuario de credenciales offline
        try {
          const savedCreds = storage.getItem(OFFLINE_CREDS_KEY);
          if (savedCreds) {
            const offlineCredentials = JSON.parse(savedCreds);
            setUser(offlineCredentials.user);
            logger.log('✅ Loaded user from offline credentials (server unavailable)');
            setLoading(false);
            return;
          }
        } catch (offlineError) {
          console.error('Error loading offline user:', offlineError);
        }
        setLoading(false);
        return; // NO hacer logout
      }
      
      // Don't logout if offline or network error - user data might be available from offline credentials
      if (!navigator.onLine || error.code === 'ERR_NETWORK' || !error.response) {
        logger.log('🔴 Network error loading user - checking offline credentials');
        
        // Try to load user from offline credentials
        try {
          const savedCreds = storage.getItem(OFFLINE_CREDS_KEY);
          if (savedCreds) {
            const offlineCredentials = JSON.parse(savedCreds);
            setUser(offlineCredentials.user);
            logger.log('✅ Loaded user from offline credentials');
            setLoading(false);
            return;
          }
        } catch (offlineError) {
          console.error('Error loading offline user:', offlineError);
        }
        setLoading(false);
        return; // NO hacer logout en error de red
      }
      
      // Only logout on real auth errors (401, 403)
      if (status === 401 || status === 403) {
        logger.log('🔴 Auth error - logging out');
        logout();
      }
    } finally {
      setLoading(false);
    }
  };

  const login = async (username, password, rememberMe = false) => {
    // Helper: hace login con reintentos automáticos en errores 5xx / network (cold start)
    // Feb-2026: timeout agresivo (10s en lugar de 30s) + solo 1 retry en lugar de 2.
    // En red mala intermitente, 30s × 3 = hasta 90s esperando antes del fallback offline.
    // Con 10s × 2 el peor caso baja a ~24s, suficiente para cold-start del backend.
    const attemptLogin = async (maxRetries = 1) => {
      let lastError = null;
      for (let attempt = 0; attempt <= maxRetries; attempt++) {
        try {
          if (attempt > 0) {
            logger.log(`[Auth] Retry ${attempt}/${maxRetries} tras error de servidor…`);
            await new Promise((r) => setTimeout(r, 4000 * attempt)); // 4s
          }
          return await axios.post(`${API}/auth/login`, {
            username,
            password,
            remember_me: rememberMe,
          }, { timeout: 10000 });
        } catch (err) {
          lastError = err;
          const status = err.response?.status;
          const isServerError = !err.response || (status >= 500 && status < 600) || err.code === 'ECONNABORTED';
          if (!isServerError) throw err; // 4xx → no reintentar (credenciales reales)
          if (attempt === maxRetries) throw err;
        }
      }
      throw lastError; // unreachable
    };

    try {
      logger.log('[Auth] Attempting login to:', `${API}/auth/login`, '(remember_me:', rememberMe, ')');
      const response = await attemptLogin(1);
      
      logger.log('[Auth] Login successful');
      
      const { access_token, refresh_token: newRefreshToken, user: userData } = response.data;
      
      // Guardar access_token
      storage.setItem(TOKEN_KEY, access_token);
      setToken(access_token);
      setUser(userData);
      axios.defaults.headers.common['Authorization'] = `Bearer ${access_token}`;
      
      // Guardar refresh_token si "Mantenerme conectado" está activo
      if (rememberMe && newRefreshToken) {
        storage.setItem(REFRESH_TOKEN_KEY, newRefreshToken);
        setRefreshToken(newRefreshToken);
        logger.log('[Auth] ✅ Refresh token guardado - sesión persistente activada');
      } else {
        // Limpiar refresh token anterior si no se pidió "recordar"
        storage.removeItem(REFRESH_TOKEN_KEY);
        setRefreshToken(null);
      }
      
      // Save credentials for offline login (hashed)
      const offlineCredentials = {
        username,
        // Simple hash for offline validation (not cryptographically secure, but better than plaintext)
        passwordHash: btoa(username + ':' + password),
        user: userData,
        savedAt: Date.now()
      };
      storage.setItem(OFFLINE_CREDS_KEY, JSON.stringify(offlineCredentials));
      
      return { success: true, isOffline: false };
    } catch (error) {
      console.error('[Auth] Login error:', {
        message: error.message,
        code: error.code,
        hasResponse: !!error.response,
        responseStatus: error.response?.status,
        responseData: error.response?.data,
        navigatorOnline: navigator.onLine
      });
      
      // If we got a response from the server (even if it's an error), it means we're online
      if (error.response) {
        const status = error.response.status;
        // 5xx tras todos los reintentos → servidor caído / cold-start prolongado
        if (status >= 500 && status < 600) {
          return {
            success: false,
            error: '⏳ El servidor está iniciando. Espera 10-20 segundos e inténtalo de nuevo.',
            retriable: true,
          };
        }
        // 401/403/422/etc. → error real de credenciales o validación
        return {
          success: false,
          error: error.response.data?.detail || 'Credenciales incorrectas'
        };
      }
      
      // Sin respuesta del servidor: timeout o sin red. Intenta offline antes de rendirse.
      logger.log('[Auth] No server response, attempting offline login...');
      
      try {
        const savedCreds = storage.getItem(OFFLINE_CREDS_KEY);
        
        if (!savedCreds) {
          return {
            success: false,
            error: '🔴 Sin conexión. No hay credenciales guardadas para login offline.'
          };
        }
        
        const offlineCredentials = JSON.parse(savedCreds);
        const expectedHash = btoa(username + ':' + password);
        
        // Validate offline credentials
        if (offlineCredentials.username === username && offlineCredentials.passwordHash === expectedHash) {
          logger.log('[Auth] Offline login successful');
          
          // Set user from saved credentials
          setUser(offlineCredentials.user);
          
          // Generate a temporary offline token
          const offlineToken = 'offline_' + Date.now();
          storage.setItem(TOKEN_KEY, offlineToken);
          setToken(offlineToken);
          
          return {
            success: true,
            isOffline: true,
            message: '🔴 Modo Offline - Datos guardados disponibles'
          };
        } else {
          return {
            success: false,
            error: '🔴 Sin conexión. Credenciales incorrectas.'
          };
        }
      } catch (offlineError) {
        console.error('[Auth] Offline login failed:', offlineError);
        return {
          success: false,
          error: '🔴 Sin conexión. Error al validar credenciales offline.'
        };
      }
    }
  };

  const logout = async () => {
    // Intentar revocar refresh tokens en el servidor
    try {
      if (token && !token.startsWith('offline_')) {
        await axios.post(`${API}/auth/logout`);
        logger.log('✅ Sesión cerrada en el servidor');
      }
    } catch (e) {
      logger.log('⚠️ No se pudo cerrar sesión en servidor (puede estar offline)');
    }
    
    // Limpiar tokens y usuario localmente
    storage.removeItem(TOKEN_KEY);
    storage.removeItem(REFRESH_TOKEN_KEY);
    setToken(null);
    setRefreshToken(null);
    setUser(null);
    delete axios.defaults.headers.common['Authorization'];
    
    // CRÍTICO: Limpiar todos los elementos de audio y Blob URLs
    logger.log('🧹 Limpiando audios al hacer logout...');
    document.querySelectorAll('audio').forEach(audio => {
      audio.pause();
      if (audio.src && audio.src.startsWith('blob:')) {
        URL.revokeObjectURL(audio.src);
        logger.log(`🗑️ Blob URL revocada: ${audio.id}`);
      }
      audio.remove();
    });
    logger.log('✅ Audios limpiados');
  };

  const value = {
    user,
    token,
    login,
    logout,
    loading,
    isAuthenticated: !!user,
    isAdmin: user?.role === 'admin',
    storageWarning
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};
